import React, { useState, useEffect } from "react";
import { 
    Box,
    Flex,
    Text,
    Grid,
    GridItem,
    List,
    ListItem,
    ListIcon, 
    Icon,
    Button,
    Link  
} from '@chakra-ui/react'
import { 
    ProfileProps, 
    Level,
    Requirements,
    GuideDirection 
} from "../../types";
import { LinkContext } from "../../LinkContext";
import { ReactComponent as BlackLine } from "../../img/blackLine.svg";
import { ReactComponent as TopArrow } from "../../img/topArrow.svg";
import { ReactComponent as BottomArrow } from "../../img/bottomArrow.svg";
import AnimateHeight from 'react-animate-height';

function declOfNum(num: number, words: string[]) {
    return words[
      num % 100 > 4 && num % 100 < 20
        ? 2
        : [2, 0, 1, 1, 1, 2][num % 10 < 5 ? Math.abs(num) % 10 : 5]
    ];
  }
  

const TableItem: React.FC<ProfileProps> = ({ profile, isLast }) => {
    const [isAdvOpen, setIsAdvOpen] = useState(false);
    const [isNoOpen, setIsNoOpen] = useState(false);
    const [requirementsArray, setRequirementsArray] = useState<Requirements[]>([]);

    useEffect(() => {
        const map = new Map<string, Requirements>();

        for (const item of [
            ...profile.no_exams, 
            ...profile.points100, 
            ...profile.individual_achievement_advantages
        ]) {
            const { requirements } = item;
            if (map.has(requirements)) {
                const existing = map.get(requirements)!;
                if ('guide_directions' in item) {
                    existing.guide_directions = [...(existing.guide_directions || []), ...item.guide_directions];
                }
                if ('subjects' in item) {
                    existing.subjects = [...(existing.subjects || []), ...item.subjects];
                }
                if ('points' in item) {
                    existing.points = item.points;
                }
            } else {
                map.set(requirements, item as Requirements);
            }
        }

        setRequirementsArray(Array.from(map.values()));
    }, [])


    return(
        <Flex 
            gap='10px' 
            flexDirection='column'
        >
            <Box as='b'>
                {profile.name}
            </Box>
            <Grid
                templateColumns='16% 84%'
                overflow="hidden"
            >
                <GridItem 
                    pr='20px'
                >
                    <Flex 
                        gap='10px' 
                        flexDirection='column'
                    >
                        {profile.levels.map((level, i) => (
                            <Flex
                                key={i}
                                p='15px 20px'
                                gap='10px'
                                bg='#F4F4F4'
                                borderRadius='4px'
                                flexDirection='column'
                            >
                                <Text>
                                    {level.year}
                                </Text>
                                <Text>
                                    Уровень: {level.level}
                                </Text>
                            </Flex>
                        ))}
                    </Flex>
                </GridItem>
                <GridItem>
                    <Flex 
                        flexDirection='column'
                        gap='20px'
                    >
                        {requirementsArray.map((req, i) => (
                            <Grid 
                                key={i}
                                bg='#F4F4F4'
                                templateColumns='39.3% 12% 21.4% 27.3%'
                                borderRadius='4px'
                            >
                                <GridItem p='20px'>
                                    <List>
                                        <ListItem key={0}>
                                            {req.requirements}
                                        </ListItem>
                                    </List>
                                </GridItem>
                                <GridItem px='10px' py='20px'>
                                    {req.points ? 
                                        req.points + declOfNum(req.points, [
                                            " балл",
                                            " балла",
                                            " баллов",
                                        ]) : 
                                        <Icon as={BlackLine}/>
                                    }

                                </GridItem>
                                <GridItem p='20px'>
                                    <List>
                                        {req.subjects && req.subjects.length > 0 ? (
                                            <>
                                                {req.subjects.slice(0, 3).map((item: string, i: number) => (
                                                    <ListItem key={i}>
                                                        {item}
                                                    </ListItem>
                                                ))}

                                                <AnimateHeight duration={500} height={isAdvOpen ? 'auto' : 0}>
                                                    {req.subjects.slice(3).map((item: string, i: number) => (
                                                        <ListItem key={i + 3}>
                                                            {item}
                                                        </ListItem>
                                                    ))}
                                                </AnimateHeight>
                                                
                                                {req.subjects.length > 3 && (
                                                    <Flex
                                                        mt="10px"
                                                        gap="5px"
                                                        alignItems="center"
                                                        color="#5FC1CF"
                                                        onClick={() => setIsAdvOpen(!isAdvOpen)}
                                                    >
                                                        <Box>{isAdvOpen ? 'Скрыть' : 'Показать все'}</Box>
                                                        <Icon h="8px" as={isAdvOpen ? TopArrow : BottomArrow} />
                                                    </Flex>
                                                )}
                                            </>
                                        ) : (
                                            <Icon as={BlackLine} />
                                        )}
                                    </List>
                                </GridItem>
                                <GridItem p='20px'>
                                    <List className="without-style">
                                        {req.guide_directions && req.guide_directions.length > 0 ? (
                                            <>
                                                {req.guide_directions.slice(0, 2).map((item: GuideDirection, i: number) => (
                                                    <ListItem py="5px" key={i}>
                                                        <Flex fontSize="18px" lineHeight="20px">
                                                            <Box pr="10px" as="b" color="#E48F0D">
                                                                {item.title.split(' ')[0]}
                                                            </Box>
                                                            <LinkContext.Consumer>
                                                                {(APP_LINK) => (
                                                                    <Link
                                                                        color="#1D438A"
                                                                        href={`${APP_LINK}/guide?eduLevel=bach-spec&direction=${item.title.split(' ')[0]}`}
                                                                        target="_blank"
                                                                    >
                                                                        {item.title.split(' ').slice(1).join(' ')}
                                                                    </Link>
                                                                )}
                                                            </LinkContext.Consumer>
                                                        </Flex>
                                                    </ListItem>
                                                ))}

                                                <AnimateHeight duration={500} height={isNoOpen ? 'auto' : 0}>
                                                    {req.guide_directions.slice(2).map((item: GuideDirection, i: number) => (
                                                        <ListItem py="5px" key={i + 2}>
                                                            <Flex fontSize="18px" lineHeight="20px">
                                                                <Box pr="10px" as="b" color="#E48F0D">
                                                                    {item.title.split(' ')[0]}
                                                                </Box>
                                                                <LinkContext.Consumer>
                                                                    {(APP_LINK) => (
                                                                        <Link
                                                                            color="#1D438A"
                                                                            href={`${APP_LINK}/guide?eduLevel=bach-spec&direction=${item.title.split(' ')[0]}`}
                                                                            target="_blank"
                                                                        >
                                                                            {item.title.split(' ').slice(1).join(' ')}
                                                                        </Link>
                                                                    )}
                                                                </LinkContext.Consumer>
                                                            </Flex>
                                                        </ListItem>
                                                    ))}
                                                </AnimateHeight>
                                                {req.guide_directions.length > 2 && (
                                                    <Flex
                                                        mt="10px"
                                                        gap="5px"
                                                        alignItems="center"
                                                        color="#5FC1CF"
                                                        onClick={() => setIsNoOpen(!isNoOpen)}
                                                    >
                                                        <Box>{isNoOpen ? 'Скрыть' : 'Показать все'}</Box>
                                                        <Icon h="8px" as={isNoOpen ? TopArrow : BottomArrow} />
                                                    </Flex>
                                                )}
                                            </>
                                        ) : (
                                            <Icon as={BlackLine} />
                                        )}
                                    </List>
                                </GridItem>
                            </Grid>
                        ))}
                    </Flex>
                </GridItem>
            </Grid>
            {!isLast &&
                <Box
                    w='100%'
                    my='50px'
                    borderBottom='2px dashed #B5B4B4'
                ></Box>
            }
        </Flex>
    )
}

export default TableItem;